import React, {forwardRef, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import useOnNavigateAway from 'hooks/useOnNavigateAway';

import Box from 'components/Box';
import TooltipButton from 'components/TooltipButton';
import TextInput from 'components/TextInput';

const PasswordInput = forwardRef(
	({id, autoComplete = 'current-password', ...otherProps}, ref) => {
		const [t] = useTranslation();
		const wrapperRef = useRef();
		const [isPasswordMasked, setIsPasswordMasked] = useState(true);

		// Remask the password when tabbing or clicking
		// outside of the element containing the input
		useOnNavigateAway(
			wrapperRef,
			() => setIsPasswordMasked(true),
			!isPasswordMasked
		);

		function togglePasswordMasking() {
			setIsPasswordMasked(prevValue => !prevValue);
		}

		const toggleButtonId = id ? `${id}-toggle-button` : null;

		return (
			<Box position="relative" ref={wrapperRef}>
				<TextInput
					{...otherProps}
					ref={ref}
					id={id}
					type={isPasswordMasked ? 'password' : 'text'}
					autoComplete={autoComplete}
					spellCheck="false"
					autoCorrect="off"
				/>
				<TooltipButton
					square
					variant="transparent"
					id={toggleButtonId}
					isActive={!isPasswordMasked}
					icon={isPasswordMasked ? 'eye-open' : 'eye-closed'}
					title={
						isPasswordMasked
							? t('reveal_password')
							: t('hide_password')
					}
					onClick={togglePasswordMasking}
					position="absolute"
					right
					top
				/>
			</Box>
		);
	}
);

PasswordInput.displayName = 'PasswordInput';

export default PasswordInput;
